import React, { useState, useEffect, useRef, useCallback } from "react";
import { FiSearch, FiX } from "react-icons/fi";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { MdScience } from "react-icons/md";
import { BiLeaf } from "react-icons/bi";
import { SearchService, SearchResult } from "../../services/searchService";
import { useDebounce } from "../../hooks/useDebounce";

interface GlobalSearchProps {
  className?: string;
  placeholder?: string;
  onResultSelect?: (result: SearchResult) => void;
}

const RESULTS_PER_PAGE = 10;

/**
 * GlobalSearch - Production-ready search component with infinite scroll
 * Features:
 * - Real-time search with debouncing
 * - Infinite scroll with intersection observer
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Click outside to close
 * - Accessibility (ARIA labels, roles)
 * - Optimized rendering and memory usage
 * - Result categorization by project type
 */
export const GlobalSearch: React.FC<GlobalSearchProps> = ({
  className = "",
  placeholder = "Search projects, methodologies, locations...",
  onResultSelect,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [totalFound, setTotalFound] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const currentQueryRef = useRef("");

  const debouncedQuery = useDebounce(query, 300);

  // Perform initial search
  useEffect(() => {
    const performSearch = async () => {
      if (debouncedQuery.trim().length < 2) {
        setResults([]);
        setTotalFound(0);
        setIsOpen(false);
        setCurrentPage(0);
        setHasMore(false);
        currentQueryRef.current = "";
        return;
      }

      // Reset on new query
      setIsLoading(true);
      setCurrentPage(0);
      currentQueryRef.current = debouncedQuery;

      try {
        const response = await SearchService.search(debouncedQuery, RESULTS_PER_PAGE);
        setResults(response.suggestions);
        setTotalFound(response.total_found);
        setHasMore(response.suggestions.length < response.total_found);
        setIsOpen(response.suggestions.length > 0);
        setSelectedIndex(-1);
      } catch (error) {
        console.error("Search failed:", error);
        setResults([]);
        setTotalFound(0);
        setHasMore(false);
      } finally {
        setIsLoading(false);
      }
    };

    performSearch();
  }, [debouncedQuery]);

  // Load more results
  const loadMoreResults = useCallback(async () => {
    if (isLoadingMore || !hasMore || !currentQueryRef.current) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;

    try {
      const response = await SearchService.search(
        currentQueryRef.current,
        RESULTS_PER_PAGE,
        nextPage * RESULTS_PER_PAGE,
      );

      setResults((prev) => [...prev, ...response.suggestions]);
      setCurrentPage(nextPage);
      setHasMore(results.length + response.suggestions.length < response.total_found);
    } catch (error) {
      console.error("Load more failed:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, currentPage, results.length]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !isLoadingMore) {
          loadMoreResults();
        }
      },
      {
        root: resultsRef.current,
        rootMargin: "100px",
        threshold: 0.1,
      },
    );

    const currentLoadMoreRef = loadMoreRef.current;
    if (currentLoadMoreRef) {
      observer.observe(currentLoadMoreRef);
    }

    return () => {
      if (currentLoadMoreRef) {
        observer.unobserve(currentLoadMoreRef);
      }
    };
  }, [hasMore, isLoadingMore, loadMoreResults]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && results[selectedIndex]) {
            handleResultClick(results[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          inputRef.current?.blur();
          break;
      }
    },
    [isOpen, results, selectedIndex],
  );

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && resultsRef.current) {
      const selectedElement = resultsRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedIndex]);

  const handleResultClick = (result: SearchResult) => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    onResultSelect?.(result);
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    setCurrentPage(0);
    setHasMore(false);
    setTotalFound(0);
    currentQueryRef.current = "";
    inputRef.current?.focus();
  };

  const getProjectTypeIcon = (projectType: string) => {
    switch (projectType.toLowerCase()) {
      case "mangrove":
        return <BiLeaf className="text-emerald-500 h-5 w-5" />;
      case "forest":
        return <BiLeaf className="h-5 w-5 text-green-600" />;
      case "carbon":
        return <MdScience className="h-5 w-5 text-blue-500" />;
      default:
        return <MdScience className="h-5 w-5 text-gray-500" />;
    }
  };

  const getConfidenceBadge = (confidence: string) => {
    const colors = {
      high: "bg-green-100 text-green-800 border-green-200",
      medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
      low: "bg-gray-100 text-gray-600 border-gray-200",
    };

    return colors[confidence as keyof typeof colors] || colors.low;
  };

  return (
    <div ref={searchRef} className={`relative w-full ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <FiSearch className="h-5 w-5 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 placeholder-gray-500 transition-all duration-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
          aria-label="Global search"
          aria-autocomplete="list"
          aria-controls="search-results"
          aria-expanded={isOpen}
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
            aria-label="Clear search"
          >
            <FiX className="h-5 w-5" />
          </button>
        )}
        {isLoading && (
          <div className="absolute inset-y-0 right-10 flex items-center pr-3">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-purple-600 border-t-transparent" />
          </div>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && results.length > 0 && (
        <div
          ref={resultsRef}
          id="search-results"
          role="listbox"
          className="absolute z-50 mt-2 max-h-[500px] w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-2xl"
        >
          {/* Results Header */}
          <div className="border-b border-gray-100 bg-gray-50 px-4 py-2">
            <p className="text-xs font-medium text-gray-600">
              Found <span className="font-semibold text-purple-600">{totalFound}</span> results
            </p>
          </div>

          {/* Results List */}
          <div className="py-1">
            {results.map((result, index) => (
              <div
                key={`${result.source_table}-${result.id}`}
                role="option"
                aria-selected={selectedIndex === index}
                onClick={() => handleResultClick(result)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`cursor-pointer border-b border-gray-50 px-4 py-3 transition-colors duration-150 last:border-b-0 ${
                  selectedIndex === index ? "bg-purple-50" : "hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className="mt-0.5 flex-shrink-0">
                    {getProjectTypeIcon(result.project_type)}
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    {/* Title */}
                    <div className="flex items-center gap-2">
                      <h4 className="truncate text-sm font-semibold text-gray-900">
                        {result.name || `${result.project_type} Project #${result.id}`}
                      </h4>
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${getConfidenceBadge(
                          result.match_context.confidence,
                        )}`}
                      >
                        {result.match_context.confidence}
                      </span>
                    </div>

                    {/* Description */}
                    {result.description && (
                      <p className="mt-1 line-clamp-2 text-xs text-gray-600">
                        {result.description}
                      </p>
                    )}

                    {/* Metadata */}
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      {result.country && (
                        <div className="flex items-center gap-1">
                          <HiOutlineLocationMarker className="h-3.5 w-3.5" />
                          <span>
                            {result.country}
                            {result.adm1 && `, ${result.adm1}`}
                          </span>
                        </div>
                      )}
                      {result.methodology && (
                        <div className="flex items-center gap-1">
                          <MdScience className="h-3.5 w-3.5" />
                          <span className="truncate">{result.methodology}</span>
                        </div>
                      )}
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                        {result.project_type}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Load More Trigger */}
          {hasMore && (
            <div
              ref={loadMoreRef}
              className="border-t border-gray-100 bg-gray-50 px-4 py-3 text-center"
            >
              {isLoadingMore ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-purple-600 border-t-transparent" />
                  <p className="text-xs text-gray-600">Loading more results...</p>
                </div>
              ) : (
                <p className="text-xs text-gray-600">
                  Showing {results.length} of {totalFound} results
                </p>
              )}
            </div>
          )}

          {/* All Results Loaded */}
          {!hasMore && results.length > 0 && results.length === totalFound && (
            <div className="border-t border-gray-100 bg-gray-50 px-4 py-2 text-center">
              <p className="text-xs text-gray-500">All {totalFound} results loaded</p>
            </div>
          )}
        </div>
      )}

      {/* No Results */}
      {isOpen && query.trim().length >= 2 && results.length === 0 && !isLoading && (
        <div className="absolute z-50 mt-2 w-full rounded-lg border border-gray-200 bg-white p-6 shadow-lg">
          <div className="text-center">
            <FiSearch className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2 text-sm font-medium text-gray-900">No results found</p>
            <p className="mt-1 text-xs text-gray-500">Try adjusting your search terms or filters</p>
          </div>
        </div>
      )}
    </div>
  );
};
